<template>
  <section class="workflow-summary">
    <aside class="actions">
      <button-fill
        v-if="isTerminateShown"
        color="secondary"
        :disabled="isTerminateDisabled"
        :disabled-label="terminateDisabledLabel"
        :label="$t('dj-中止')"
        @click.prevent="$modal.show('confirm-termination')"
      />
    </aside>

    <modal name="confirm-termination">
      <h3>{{ $t("dj-确定要中止此实例吗?") }}</h3>
      <input v-model="terminationReason" :placeholder="$t('dj-中止原因')" />
      <footer>
        <button-fill
          color="secondary"
          :label="$t('dj-中止')"
          name="button-terminate"
          @click.prevent="terminate"
        />

        <button-fill
          color="primary"
          :label="$t('dj-取消')"
          name="button-cancel"
          @click.prevent="$modal.hide('confirm-termination')"
        />
      </footer>
    </modal>

    <dl v-if="workflow">
      <div v-if="workflow.workflowExecutionInfo.isArchived">
        <h5>Workflow archived</h5>
        <p>
          This workflow has been retrieved from archival. Some summary
          information may be missing.
        </p>
      </div>
      <div class="started-at">
        <dt>{{ $t("dj-开始时间") }}</dt>
        <dd>{{ workflowStartTime }}</dd>
      </div>
      <div v-if="workflowCloseTime" class="close-time">
        <dt>{{ $t("dj-结束时间") }}</dt>
        <dd>{{ workflowCloseTime }}</dd>
      </div>
      <div
        class="workflow-status"
        :data-status="
          wfStatus !== undefined &&
          (typeof wfStatus === 'string' ? wfStatus : wfStatus.status)
        "
      >
        <dt>{{ $t("dj-状态") }}</dt>
        <dd>
          <bar-loader v-if="wfStatus === 'running'" />
          <span v-if="typeof wfStatus === 'string'">{{ wfStatus }}</span>
          <router-link
            v-if="wfStatus !== undefined && wfStatus.to"
            :to="wfStatus.to"
          >
            {{ wfStatus.text }}
          </router-link>
        </dd>
      </div>
      <div v-if="result" class="workflow-result">
        <dt>{{ $t("dj-结果") }}</dt>
        <dd>
          <data-viewer :item="result" :title="displayWorkflowId + ' Result'" />
        </dd>
      </div>
      <div class="workflow-id">
        <dt>{{ $t("dj-流程") }} Id</dt>
        <dd>{{ displayWorkflowId }}</dd>
      </div>
      <div class="run-id">
        <dt>Run Id</dt>
        <dd>{{ runId }}</dd>
      </div>
      <div v-if="parentWorkflowRoute" class="parent-workflow">
        <dt>{{ $t("dj-父流程") }}</dt>
        <dd>
          <router-link :to="parentWorkflowRoute.to">
            {{ parentWorkflowRoute.text }}
          </router-link>
        </dd>
      </div>
      <div class="task-list">
        <dt>{{ $t("dj-任务列表") }}</dt>
        <dd>
          <router-link
            :to="{
              name: 'task-list',
              params: {
                clusterName,
                taskList: workflow.executionConfiguration.taskList.name,
              },
            }"
          >
            {{ workflow.executionConfiguration.taskList.name }}
          </router-link>
        </dd>
      </div>
      <div class="workflow-input">
        <dt>Input {{ $t("dj-参数") }}</dt>
        <dd>
          <data-viewer
            v-if="input !== undefined"
            :item="input"
            :title="displayWorkflowId + ' Input'"
          />
        </dd>
      </div>
      <div v-if="workflow.pendingActivities" class="pending-activities">
        <dt>Pending {{ $t("dj-活动") }}</dt>
        <dd v-for="pa in workflow.pendingActivities" :key="pa.activityID">
          <detail-list :item="pa" />
        </dd>
      </div>
    </dl>
  </section>
</template>
<script>
import i18n from "@/assets/i18n/translate-message";
import {
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
} from "@/constants";
import { getErrorMessage, getDatetimeFormattedString } from "@/helpers";
import { BarLoader, ButtonFill, DataViewer, DetailList } from "@/components";
import api from "@/api";

export default {
  name: "Summary",
  components: {
    "bar-loader": BarLoader,
    "button-fill": ButtonFill,
    "data-viewer": DataViewer,
    "detail-list": DetailList,
  },
  props: [
    "baseAPIURL",
    "clusterName",
    "dateFormat",
    "displayWorkflowId",
    "domain",
    "input",
    "isWorkflowRunning",
    "parentWorkflowRoute",
    "result",
    "runId",
    "timeFormat",
    "timezone",
    "wfStatus",
    "workflow",
  ],
  data() {
    return {
      isAuthorized: false,
      isWorkflowTerminateFeatureFlagEnabled: false,
      terminationReason: undefined,
    };
  },
  computed: {
    isTerminateShown() {
      return this.isWorkflowTerminateFeatureFlagEnabled && this.isAuthorized;
    },
    isTerminateDisabled() {
      return !this.isWorkflowRunning;
    },
    terminateDisabledLabel() {
      return !this.isWorkflowRunning ? i18n.t("dj-运行中实例才可中止") : "";
    },
    workflowCloseTime() {
      const { dateFormat, timeFormat, timezone } = this;
      const { closeTimeInString } = this.workflow.workflowExecutionInfo;

      return closeTimeInString
        ? getDatetimeFormattedString({
            date: closeTimeInString,
            dateFormat,
            timeFormat,
            timezone,
          })
        : "";
    },
    workflowStartTime() {
      const { dateFormat, timeFormat, timezone } = this;
      const { startTimeInString } = this.workflow.workflowExecutionInfo;

      return getDatetimeFormattedString({
        date: startTimeInString,
        dateFormat,
        timeFormat,
        timezone,
      });
    },
  },
  async mounted() {
    this.isWorkflowTerminateFeatureFlagEnabled = true;
    this.isAuthorized = true;
  },
  methods: {
    async terminate() {
      this.$modal.hide("confirm-termination");
      const res = await api.teminateWorkflow(
        this.displayWorkflowId,
        this.runId,
        this.terminationReason
      );
      if (res.data.status === 200) {
        this.$emit("onNotification", {
          message: i18n.t("dj-实例已中止"),
          type: NOTIFICATION_TYPE_SUCCESS,
        });
      } else {
        this.$emit("onNotification", {
          message: getErrorMessage(
            i18n.t("dj-中止失败"),
            i18n.t("dj-中止错误，请检查并重试")
          ),
          type: NOTIFICATION_TYPE_ERROR,
        });
      }
    },
  },
};
</script>

<style lang="stylus">
@require "../../styles/definitions.styl"

section.workflow-summary
  overflow auto
  padding layout-spacing-small

  .pending-activities {
    dl.details {
      dd {
        white-space: normal;
      }
    }
  }

  dl:not(.details)
    & > div
      margin-bottom 1em
      & > dt
        text-transform uppercase
        font-size 11px
      dd, dt
        line-height 1.5em
  dl.details
    border 1px solid uber-black-60
    margin-bottom 1em
    dt
      padding 0 4px
  .run-id, .task-list, .workflow-id, .workflow-name
    dd
      font-weight 300
      font-family monospace-font-family
  .workflow-status
    dd
      text-transform capitalize
    &[data-status="completed"] dd
      color uber-green
    &[data-status="failed"] dd
      color uber-orange
  pre
    max-height 18vh

  aside.actions
    float right
    a
      action-button(uber-orange)

[data-modal="confirm-termination"]
  input
    margin layout-spacing-medium 0
  footer
    display flex
    justify-content space-between
</style>
